import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { WebSocketConnection, WebSocketMessage } from '../types/multijugador';

export class WebSocketService {
  private static instance: WebSocketService;
  private stompClient: Client | null = null;
  private connectionState: WebSocketConnection = {
    connected: false,
    connecting: false,
    error: undefined
  };
  private subscriptions: Map<string, any> = new Map();
  private messageHandlers: Map<string, (message: any) => void> = new Map();

  private constructor() {}

  /**
   * Obtener instancia singleton
   */
  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  /**
   * Conectar al WebSocket
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.connectionState.connected) {
        resolve();
        return;
      }

      if (this.connectionState.connecting) {
        // Ya se está conectando, esperar
        const checkConnection = () => {
          if (this.connectionState.connected) {
            resolve();
          } else if (this.connectionState.error) {
            reject(new Error(this.connectionState.error));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
        return;
      }

      this.connectionState.connecting = true;
      this.connectionState.error = undefined;

      const wsUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/ws`;

      this.stompClient = new Client({
        webSocketFactory: () => new SockJS(wsUrl) as any,
        debug: (str) => {
          if (process.env.NODE_ENV === 'development') {
            console.log(str);
          }
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
        onConnect: (frame) => {
          console.log('🔗 WebSocket conectado:', frame);
          this.connectionState = {
            connected: true,
            connecting: false,
            error: undefined
          };
          resolve();
        },
        onStompError: (frame) => {
          console.error('❌ Error de STOMP:', frame);
          const error = frame.headers?.message || 'Error desconocido';
          this.connectionState = {
            connected: false,
            connecting: false,
            error: error
          };
          reject(new Error(error));
        },
        onWebSocketError: (event) => {
          console.error('❌ Error de WebSocket:', event);
          this.connectionState = {
            connected: false,
            connecting: false,
            error: 'Error de conexión WebSocket'
          };
          reject(new Error('Error de conexión WebSocket'));
        },
        onWebSocketClose: (event) => {
          console.warn('🔌 WebSocket cerrado:', event);
          this.connectionState = {
            connected: false,
            connecting: false,
            error: undefined
          };
        },
        onDisconnect: (frame) => {
          console.log('🔌 Desconectado de STOMP:', frame);
          this.connectionState = {
            connected: false,
            connecting: false,
            error: undefined
          };
        }
      });

      this.stompClient.activate();
    });
  }

  /**
   * Desconectar del WebSocket
   */
  disconnect(): void {
    if (this.stompClient && this.connectionState.connected) {
      // Desuscribirse de todos los tópicos
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
      this.subscriptions.clear();
      this.messageHandlers.clear();

      this.stompClient.deactivate();
      this.connectionState = {
        connected: false,
        connecting: false,
        error: undefined
      };
      console.log('🔌 WebSocket desconectado');
    }
  }

  /**
   * Suscribirse a un tópico
   */
  subscribe(topic: string, handler: (message: any) => void): void {
    if (!this.connectionState.connected || !this.stompClient) {
      console.warn('WebSocket no conectado. Conectando primero...');
      this.connect().then(() => {
        this.subscribe(topic, handler);
      });
      return;
    }

    // Si ya existe una suscripción, desuscribirse primero
    if (this.subscriptions.has(topic)) {
      this.subscriptions.get(topic).unsubscribe();
    }

    const subscription = this.stompClient.subscribe(topic, (message: any) => {
      try {
        const parsedMessage = JSON.parse(message.body);
        console.log(`📨 Mensaje recibido en ${topic}:`, parsedMessage);
        handler(parsedMessage);
      } catch (error) {
        console.error('Error al parsear mensaje WebSocket:', error);
        handler(message.body);
      }
    });

    this.subscriptions.set(topic, subscription);
    this.messageHandlers.set(topic, handler);
    console.log(`📡 Suscrito a ${topic}`);
  }

  /**
   * Desuscribirse de un tópico
   */
  unsubscribe(topic: string): void {
    if (this.subscriptions.has(topic)) {
      this.subscriptions.get(topic).unsubscribe();
      this.subscriptions.delete(topic);
      this.messageHandlers.delete(topic);
      console.log(`📡❌ Desuscrito de ${topic}`);
    }
  }

  /**
   * Enviar mensaje
   */
  send(destination: string, message: any): void {
    if (!this.connectionState.connected || !this.stompClient) {
      console.error('WebSocket no conectado. No se puede enviar mensaje.');
      return;
    }

    this.stompClient.publish({
      destination: destination,
      body: JSON.stringify(message)
    });
    console.log(`📤 Mensaje enviado a ${destination}:`, message);
  }

  /**
   * Obtener estado de conexión
   */
  getConnectionState(): WebSocketConnection {
    return { ...this.connectionState };
  }

  /**
   * Verificar si está conectado
   */
  isConnected(): boolean {
    return this.connectionState.connected;
  }

  // ========================================
  // MÉTODOS ESPECÍFICOS PARA MULTIJUGADOR
  // ========================================

  /**
   * Suscribirse a eventos de una sala específica
   */
  suscribirseASala(codigoSala: string, handlers: {
    onParticipanteUnido?: (participante: any) => void;
    onParticipanteDesconectado?: (participante: any) => void;
    onInicioExamen?: () => void;
    onActualizacionRanking?: (ranking: any) => void;
    onSalaFinalizada?: () => void;
    onSalaCancelada?: () => void;
  }): void {
    const baseTopicPath = `/topic/sala/${codigoSala}`;

    if (handlers.onParticipanteUnido) {
      this.subscribe(`${baseTopicPath}/participante-unido`, handlers.onParticipanteUnido);
    }

    if (handlers.onParticipanteDesconectado) {
      this.subscribe(`${baseTopicPath}/participante-desconectado`, handlers.onParticipanteDesconectado);
    }

    if (handlers.onInicioExamen) {
      this.subscribe(`${baseTopicPath}/inicio-examen`, handlers.onInicioExamen);
    }

    if (handlers.onActualizacionRanking) {
      this.subscribe(`${baseTopicPath}/ranking`, handlers.onActualizacionRanking);
    }

    if (handlers.onSalaFinalizada) {
      this.subscribe(`${baseTopicPath}/finalizada`, handlers.onSalaFinalizada);
    }

    if (handlers.onSalaCancelada) {
      this.subscribe(`${baseTopicPath}/cancelada`, handlers.onSalaCancelada);
    }
  }

  /**
   * Desuscribirse de todos los eventos de una sala
   */
  desuscribirseDeASala(codigoSala: string): void {
    const baseTopicPath = `/topic/sala/${codigoSala}`;
    const topics = [
      `${baseTopicPath}/participante-unido`,
      `${baseTopicPath}/participante-desconectado`,
      `${baseTopicPath}/inicio-examen`,
      `${baseTopicPath}/ranking`,
      `${baseTopicPath}/finalizada`,
      `${baseTopicPath}/cancelada`
    ];

    topics.forEach(topic => this.unsubscribe(topic));
  }

  /**
   * Enviar ping para mantener conexión activa
   */
  enviarPing(): void {
    this.send('/app/ping', { 
      timestamp: Date.now(),
      userAgent: navigator.userAgent 
    });
  }

  /**
   * Configurar ping automático cada 30 segundos
   */
  configurarPingAutomatico(): NodeJS.Timeout {
    return setInterval(() => {
      if (this.isConnected()) {
        this.enviarPing();
      }
    }, 30000);
  }
}